import { forwardRef, type HTMLProps, type LegacyRef } from 'react';
import assert from 'assert';
import type {   
  ApiReturnUser, 
  ApiUserUpdateDTO,
} from '@highimpact-frontend/api-types';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { isDataState } from '../../store/data/state';
import StateDecorator from '../utils/state/StateDecorator';
import { getOrElse, recordNotEqual } from '../../utils/functions';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import UserService from '../../api/services/config/user/UserService';
import { useSubmit } from '../../utils/hooks/useSubmit';
import CustomizedDialog from '../utils/MUISpecifics/dialog/CustumizedDialog';
import { Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import TextInput from '../utils/MUISpecifics/inputText/TextInput';
import DropDownItemSelector from '../utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import InputMultiType from '../utils/MUISpecifics/inputMultiType/InputMultiType';
import { resetExternalState } from '../../store/data/slice';
import { useTranslation } from 'react-i18next';

// Component props.
export interface FormProps extends HTMLProps<HTMLInputElement> {
  onCancel: () => void;
  user: ApiReturnUser | null;
  isModalOpen: boolean;
  title: string;
}

// Component props.
export interface FormPropsNotNull extends FormProps {
  user: ApiReturnUser;
}

function userNotNull(
  props: FormProps | FormPropsNotNull
): props is FormPropsNotNull {
  return Boolean(props.user);
}

function EditUserFormOrNothing(props: FormProps) {
  if (!userNotNull(props)) return <></>;
  else {
    return <EditUserForm {...props} />;
  }
}

// Component.
function EditUserForm({ 
  isModalOpen, 
  user, 
  onCancel, 
  children, 
  ...props 
}: FormPropsNotNull,
  ref: LegacyRef<HTMLDivElement> | undefined
) {

  // use translation here: 
  const { t } = useTranslation('components.users');

  const dispatch = useAppDispatch();
  const { submit, state } = useSubmit();
  const users = useAppSelector((state) => state.data_slice.externals.users);
  const companies = useAppSelector((state) => state.data_slice.externals.companies);
  const roles = useAppSelector((state) => state.data_slice.externals.roles);
  

  const defaultValues: ApiUserUpdateDTO = {    
    name: user.name || '',
    surname: user.surname || '',
    email: user.email || '',
    tel: user.tel || '', 
    notification_email: user.notification_email || true,
    notification_sms: user.notification_sms || true, 
    role_id: user.role_id || null,
    company_id: user.company_id || null,
  }; 
  
  // Handle form submission.
  const onSubmit = async (data: ApiUserUpdateDTO) => {    
    submit({
      callbacks: [
        async () => await UserService.update(user.id, data),
        () => console.log('Success:'),
        () => methods.reset(),
        () => onCancel(),
        () => dispatch(resetExternalState({ external_key: 'users'})),
        () => dispatch(resetExternalState({ external_key: 'companies'})),
      ],
      errorMessages: {
        anyError: t("Une erreur est survenue lors de la modification de l'utilisateur"),
        unicityError: t("Un utilisateur avec le même email existe déjà"),
      },
      successMessage: t("L'utilisateur a été modifié avec succès"),
    })
  }

  /**This is the simplest regular expression, it checks:
     * that there is an @
     * that there are characters before and after the @
     * that there are no spaces
     * allows any number of characters before and after the @ 
    */
    const regEmail = /^[^\s@]+@[^\s@]+$/;
    const regPhone = /^\d{10}$/;
  
    /**
     * Create validation rule for each field.
     */
    const validator = createValidator< ApiUserUpdateDTO>({    
      name: [
        (n) => !n || n.trim().length < 1 ? t('Le champ ne peut pas etre vide') : undefined,
      ],
      surname: [(n) => !n || n.trim().length < 1 ? t('Le champ ne peut pas etre vide') : undefined,],
      company_id: [(n) => (!n ? t('Sélectionner une entreprise') : undefined)],
      email: [
        (n) => (!n ? t('Entrer un email') : undefined),
        (n) => (!regEmail.test(n) ? t('Email invalide') : undefined),
      ],
      tel: [
        (n) => {
          if (n === null || n === undefined || n === '') return undefined;
          return regPhone.test(n) ? undefined 
            : t('Le numéro doit contenir exactement 10 chiffres ou rester vide')
        }
      ],
      role_id: [(n) => (!n ? t('Sélectionner un Rôle') : undefined)]
    });
  
    // Define form hook.
    const methods = useForm(defaultValues, validator, onSubmit);

  return StateDecorator([users, roles, companies], ref!, () => {
    assert(isDataState(users));
    assert(isDataState(roles));
    assert(isDataState(companies));    

    // Transform users to a list of values.
    const roleList = Object.values(roles.data);
    const companyList = Object.values(companies.data);

    return (
      <CustomizedDialog
        title={props.title}
        onCancel={() => {
          onCancel(); 
          methods.reset(); 
        }} 
        open={isModalOpen }>
        <form
          onSubmit={methods.onSubmit}
          className={`form ${props.className || ''}`}
        >

          <Stack spacing={2}>
            <TextInput
              label={t("Prénom")}
              name="name"
              placeholder="Entrer le prénom d'utilisateur"
              onChange={(value: string) => {
                methods.setValue('name', value);
              }}
              required
              defaultValue={methods.watch('name')}          
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('name') }
                  : { tag: 'Not submitted' }
              }
            />
          
            <TextInput
              label={t("Nom")}
              name="surname"
              placeholder="Entrer le nom d'utilisateur"
              onChange={
                (value) => {
                  methods.setValue('surname', value)
                }
              }
              required
              defaultValue={methods.watch('surname')}
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('surname') }
                  : { tag: 'Not submitted' }
              }
            />
          
            <DropDownItemSelector
              onSelect={(selectedId) => {                
                methods.setValue("company_id", selectedId)
              }}
              values={
                companyList.map((company) => ({
                  id: company.id,
                  name: getOrElse(company.name),
                }))
              }
              defaultValue={                            
                methods.watch('company_id') ?? null
              }
              label={t("Entreprise d'appartenance")}
              disabled={false}
              name={"company_id"}    
              required            
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('company_id'),
                    }
                  : { tag: 'Not submitted' }
            }/>
          
            <InputMultiType
              type='email'
              label={t("Email")}                
              name="email"              
              onChange={(value) => {
                methods.setValue('email', value)
              }}
              required
              defaultValue={methods.watch('email')}
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('email') }
                  : { tag: 'Not submitted' }
              }
            />   
          
            <InputMultiType
              label={t("Numéro de téléphone")}
              type="tel"
              name="tel" 
              defaultValue={methods.watch('tel')}               
              onChange={(value) => {
                const v = value.toString()
                methods.setValue('tel', v)
              }}              
              errors={
                methods.hasSubmitted
                  ? { tag: 'Submited', errors: methods.getErrors('tel') }
                  : { tag: 'Not submitted' }
              }
            />
          
            <DropDownItemSelector
              onSelect={(selectedId) => {                
                methods.setValue("role_id", selectedId)
              }}
              values={
                roleList.map((role) => ({                    
                  id: role.id,
                  name: getOrElse(role.name)
                }))
              }
              label={t("Rôle")}
              disabled={false}
              name={"role_id"}    
              required                          
              defaultValue={methods.watch('role_id') ?? null}      
              errors={
                methods.hasSubmitted
                  ? {
                      tag: 'Submited',
                      errors: methods.getErrors('role_id'),
                    }
                  : { tag: 'Not submitted' }
            }/>
                        
            <Button 
              type="submit" 
              className="form-button-submit"
              variant="outlined"
              loadingPosition="end"
              data-testid="submit-project-button"
              endIcon={<SaveIcon />}
              //in this case button Modifier will be disabled if the user did not changes
              disabled={!recordNotEqual(defaultValues, methods.watchAll())} 
              >
              {state === 'Loading' ? t('Envoi...') : t('Modifier')}
            </Button>
          </Stack>          
        </form>
      </CustomizedDialog>
    );
  });
}

export default forwardRef(EditUserFormOrNothing);
